/**
 * @fileoverview Shared widget type definitions and category groupings for `DashboardCanvasBuilder`.
 * @module ui-admin/components/DashboardCanvasBuilder/widgetTypes
 *
 * Responsibilities: - Define `WIDGET_TYPES` and `WIDGET_CATEGORIES` consumed by `CanvasWidgetPalette`,
 *                     `CanvasWidgetCard`, and `dashboardCanvasDragUtils`.
 * Dependencies:     - React, lucide-react
 *                   - @firesquirrel/local-db (DashboardWidget)
 * Architecture:     - Pure static metadata; zero data access.
 * Notes:            - Extracted from `DashboardBuilderPage.tsx` and `SystemDashboardBuilderPage.tsx`
 *                     to eliminate duplication (G-41).
 */

import type React from 'react';
import {
  Hash,
  BarChart3,
  Table2,
  FileText,
  Filter,
  List,
  TrendingUp,
  Minus,
  Image,
  MapPin,
  Gauge,
  Calendar,
} from 'lucide-react';
import type { DashboardWidget } from '@firesquirrel/local-db';

export type WidgetType = DashboardWidget['type'];

export type WidgetCategory =
  | 'KPIs & Gauges'
  | 'Charts'
  | 'Data Tables & Feeds'
  | 'Filters & Layout'
  | 'Spatial & Schedule';

/**
 * @contract WidgetTypeOption
 * Purpose: Metadata for a dashboard widget type in the palette and canvas cards.
 * Fields:  type, label, icon, description, category, defaultW, defaultH, minW, minH
 * Rules:   `type` is a valid `DashboardWidget['type']`.
 * Sync:    N/A.
 */
export interface WidgetTypeOption {
  type: WidgetType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  category: WidgetCategory;
  defaultW: number;
  defaultH: number;
  minW: number;
  minH: number;
}

export const WIDGET_CATEGORIES: WidgetCategory[] = [
  'KPIs & Gauges',
  'Charts',
  'Data Tables & Feeds',
  'Filters & Layout',
  'Spatial & Schedule',
];

export const WIDGET_TYPES: WidgetTypeOption[] = [
  {
    type: 'kpi',
    label: 'KPI Card',
    icon: Hash,
    description: 'Single metric + trend indicator',
    category: 'KPIs & Gauges',
    defaultW: 3,
    defaultH: 2,
    minW: 2,
    minH: 2,
  },
  {
    type: 'gauge',
    label: 'Gauge',
    icon: Gauge,
    description: 'Progress/percentage dial',
    category: 'KPIs & Gauges',
    defaultW: 3,
    defaultH: 3,
    minW: 2,
    minH: 2,
  },
  {
    type: 'progressBar',
    label: 'Progress Bar',
    icon: TrendingUp,
    description: 'Target tracking bar',
    category: 'KPIs & Gauges',
    defaultW: 4,
    defaultH: 2,
    minW: 2,
    minH: 1,
  },
  {
    type: 'chart',
    label: 'Chart',
    icon: BarChart3,
    description: 'Bar, line, pie, area, radar, donut',
    category: 'Charts',
    defaultW: 6,
    defaultH: 4,
    minW: 3,
    minH: 3,
  },
  {
    type: 'table',
    label: 'Data Table',
    icon: Table2,
    description: 'Mini tabular grid, sortable',
    category: 'Data Tables & Feeds',
    defaultW: 6,
    defaultH: 4,
    minW: 4,
    minH: 3,
  },
  {
    type: 'list',
    label: 'Activity Feed',
    icon: List,
    description: 'Icon + text rows (recent items)',
    category: 'Data Tables & Feeds',
    defaultW: 4,
    defaultH: 4,
    minW: 3,
    minH: 2,
  },
  {
    type: 'filter',
    label: 'Global Filter',
    icon: Filter,
    description: 'Filter affecting dashboard widgets',
    category: 'Filters & Layout',
    defaultW: 4,
    defaultH: 2,
    minW: 2,
    minH: 1,
  },
  {
    type: 'text',
    label: 'Text Block',
    icon: FileText,
    description: 'Markdown or rich instructions block',
    category: 'Filters & Layout',
    defaultW: 4,
    defaultH: 2,
    minW: 2,
    minH: 1,
  },
  {
    type: 'divider',
    label: 'Divider',
    icon: Minus,
    description: 'Visual separator line or space',
    category: 'Filters & Layout',
    defaultW: 12,
    defaultH: 1,
    minW: 2,
    minH: 1,
  },
  {
    type: 'image',
    label: 'Image / Logo',
    icon: Image,
    description: 'Static image or department emblem',
    category: 'Filters & Layout',
    defaultW: 3,
    defaultH: 3,
    minW: 2,
    minH: 2,
  },
  {
    type: 'map',
    label: 'Map (T2+)',
    icon: MapPin,
    description: 'Geographic pins or heatmap',
    category: 'Spatial & Schedule',
    defaultW: 6,
    defaultH: 4,
    minW: 4,
    minH: 3,
  },
  {
    type: 'calendar',
    label: 'Calendar (T2+)',
    icon: Calendar,
    description: 'Mini calendar or upcoming schedule',
    category: 'Spatial & Schedule',
    defaultW: 4,
    defaultH: 4,
    minW: 3,
    minH: 3,
  },
];
